// Der EINE Seitenkopf (Referenz: docs/design/01-roadmap-expanded.png).
//
// WOFUER: ersetzt die im Audit gefundenen 8 verschiedenen Seitenkopf-Bauarten
// ueber 15 Routen. Grosser Seitentitel als `h1`, darunter optional die
// Ansichts-Tabs (ViewTabs), rechts die Aktionen, unten eine Haarlinie.
//
// Gedacht als erstes Kind einer `PageShell`: der klebende Hintergrund laeuft
// per negativer Aussenabstaende bis an die Raender der Huelle, der Inhalt bleibt
// aber auf der Spalte der Huelle ausgerichtet.

import type { JSX, ComponentChildren } from "preact";
import { ChevronRight } from "lucide-preact";
import { Link } from "wouter-preact";

export interface Crumb {
  label: string;
  href?: string;
}

export interface PageHeaderProps {
  title: ComponentChildren;
  subtitle?: string;
  breadcrumb?: Crumb[];
  /** Rechts neben dem Titel, z.B. Button-Gruppe. */
  actions?: ComponentChildren;
  /** Hier wird ViewTabs eingehaengt. */
  tabs?: ComponentChildren;
  sticky?: boolean;
  class?: string;
}

// Bleeding: den klebenden Hintergrund bis an die Raender der PageShell ziehen.
const BLEED = "-mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8";

export function PageHeader(props: PageHeaderProps): JSX.Element {
  const {
    title,
    subtitle,
    breadcrumb,
    actions,
    tabs,
    sticky = true,
    class: klass,
  } = props;

  const stickyCls = sticky
    ? `sticky top-0 z-30 bg-[color:var(--color-surface-container-lowest)] ${BLEED} pt-2`
    : "";

  return (
    <header
      class={`mb-4 border-b border-[color:var(--color-outline-variant)]${
        stickyCls ? ` ${stickyCls}` : ""
      }${klass ? ` ${klass}` : ""}`}
    >
      {breadcrumb && breadcrumb.length > 0 && (
        <nav aria-label="Brotkrümelnavigation" class="mb-1">
          <ol class="flex flex-wrap items-center gap-1 text-xs text-[color:var(--color-on-surface-variant)]">
            {breadcrumb.map((c, i) => (
              // min-w-0 + max-w-full: ohne sie wuchs ein langer Projektname im
              // Brotkrumen ueber den Rand (390px: Seite scrollte seitlich), das
              // `truncate` am Text griff nie.
              <li key={`${c.label}-${i}`} class="flex min-w-0 max-w-full items-center gap-1">
                {i > 0 && (
                  <ChevronRight size={14} aria-hidden="true" class="shrink-0 opacity-60" />
                )}
                {c.href ? (
                  <Link
                    href={c.href}
                    class="min-w-0 truncate hover:text-[color:var(--color-on-surface)] hover:underline"
                    title={c.label}
                  >
                    {c.label}
                  </Link>
                ) : (
                  <span class="min-w-0 truncate" title={c.label}>
                    {c.label}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}

      {/* Titel und Aktionen brechen auf schmalen Fenstern um statt zu quetschen.
          `min-w-0` auf dem Titelbereich ist dokumentierte Projekt-Konvention. */}
      <div class="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 pb-3">
        {/* `basis-full` auf schmalen Fenstern ist der entscheidende Teil:
            `min-w-0 flex-1` allein HEBELT `flex-wrap` AUS — weil der Titel auf
            0 schrumpfen DARF, bricht die Zeile nie um, sondern quetscht. Bei
            390 px blieben dem Titel neben einem `shrink-0`-Aktionsknopf rund
            60 px: die Ueberschrift stand als „Da…" da und der Untertitel lief
            ueber vier Zeilen. Mit `basis-full` nimmt der Titel schmal die ganze
            Zeile und die Aktionen rutschen darunter; ab `sm` teilen sie sich
            die Reihe wieder. (Gefunden per Screenshot, kein Test sieht das.) */}
        <div class="min-w-0 basis-full sm:basis-0 sm:flex-1">
          <h1
            class="truncate text-2xl font-bold tracking-tight text-[color:var(--color-on-surface)]"
            title={typeof title === "string" ? title : undefined}
          >
            {title}
          </h1>
          {subtitle && (
            <p class="mt-0.5 text-sm text-[color:var(--color-on-surface-variant)]">
              {subtitle}
            </p>
          )}
        </div>
        {actions && (
          <div class="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>

      {/* Die Tab-Unterstriche liegen auf der Haarlinie des Kopfes. */}
      {tabs && <div class="-mb-px">{tabs}</div>}
    </header>
  );
}
